import { faChartLine } from "@fortawesome/free-solid-svg-icons";
import { useShownColorScheme } from "hooks/useShownColorScheme";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  ActionIcon,
  alpha,
  Combobox,
  DefaultMantineColor,
  Group,
  InputBase,
  Menu,
  SegmentedControl,
  Text,
  useCombobox,
  useMantineTheme,
} from "@mantine/core";
import { Routing } from "config/enums/routings";
import { useTranslation } from "react-i18next";
import { getYAxisOptions } from "utils/chart/yAxisOptions";

type Props = {
  routing: Routing;
  xAxisType: string | null;
  yAxisType: string | null;
  setXAxisType: (value: string | null) => void;
  setYAxisType: (value: string | null) => void;
  getYAxis: () => (string | null)[] | [];
  getTooltipYAxisIcon: (color?: DefaultMantineColor) => JSX.Element;
  disabledXAxisToggle: boolean;
};

export const ChartPanelMenu = ({
  xAxisType,
  yAxisType,
  setXAxisType,
  setYAxisType,
  getYAxis,
  getTooltipYAxisIcon,
  disabledXAxisToggle,
}: Props) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const computedColorScheme = useShownColorScheme();
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: (eventSource) => {
      if (eventSource === "keyboard") {
        combobox.selectActiveOption();
      } else {
        combobox.updateSelectedOptionIndex("active");
      }
    },
  });

  return (
    <Menu
      shadow="md"
      position="top"
      withinPortal
      closeOnItemClick={false}
      styles={{
        dropdown: {
          left: undefined,
          right: "0px",
        },
        item: {
          gap: 10,
          paddingTop: 5,
          paddingBottom: 5,
          "&[data-hovered]": { backgroundColor: "transparent" },
          "&:hover": { backgroundColor: "transparent" },
          cursor: "default",
        },
        itemLabel: { whiteSpace: "nowrap" },
        itemSection: {
          // Same width as the track line data select (HotlineIcon), longer labels still widen it
          minWidth: 240,
          display: "flex",
          justifyContent: "flex-start",
        },
      }}
    >
      <Menu.Target>
        <ActionIcon
          variant="light"
          sx={(theme, u) => ({
            [u.dark]: {
              backgroundColor: alpha(theme.colors.gray[9], 0.8),
            },
            [u.light]: {
              backgroundColor: alpha(theme.colors.violet[2], 0.8),
            },
            "&:hover": {
              color: theme.colors.violet[7],
            },
            color: theme.colors.violet[6],
            position: "absolute",
            width: 20,
            height: 20,
            top: 24,
            right: 16,
            zIndex: 100,
          })}
        >
          {!yAxisType ? (
            <FontAwesomeIcon icon={faChartLine} />
          ) : (
            getTooltipYAxisIcon(theme.primaryColor)
          )}
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          component="div"
          rightSection={
            <Combobox
              store={combobox}
              resetSelectionOnOptionHover
              withinPortal={false}
              onOptionSubmit={(value) => {
                setYAxisType(value);
                combobox.updateSelectedOptionIndex("active");
                combobox.closeDropdown();
              }}
            >
              <Combobox.Target targetType="button">
                <InputBase
                  component="button"
                  type="button"
                  pointer
                  // Fills the item section, the dropdown list takes the same width
                  w="100%"
                  rightSection={<Combobox.Chevron />}
                  rightSectionPointerEvents="none"
                  onClick={() => combobox.toggleDropdown()}
                >
                  <Group wrap="nowrap">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.75rem",
                        width: "100%",
                      }}
                    >
                      <span style={{ width: "0.75rem" }}>
                        {
                          getYAxisOptions(
                            t,
                            undefined,
                            computedColorScheme === "dark" ? "inherit" : "#000"
                          ).find((item) => item.value === yAxisType)?.icon
                        }
                      </span>
                      <Text size="sm">
                        {
                          getYAxisOptions(t).find(
                            (item) => item.value === yAxisType
                          )?.label
                        }
                      </Text>
                    </div>
                  </Group>
                </InputBase>
              </Combobox.Target>
              <Combobox.Dropdown>
                <Combobox.Options>
                  {getYAxisOptions(t, getYAxis()).map(
                    ({ value, label, icon }) => {
                      const isSelected = yAxisType === value;

                      return (
                        <Combobox.Option
                          sx={(theme, u) => ({
                            color: isSelected ? "white" : "inherit",
                            backgroundColor: isSelected
                              ? theme.colors.violet[5]
                              : "inherit",
                            "&:hover": {
                              [u.dark]: {
                                backgroundColor: isSelected
                                  ? theme.colors.violet[6]
                                  : theme.colors.dark[4],
                              },
                              [u.light]: {
                                backgroundColor: isSelected
                                  ? theme.colors.violet[6]
                                  : theme.colors.gray[1],
                              },
                            },
                          })}
                          value={value}
                          key={value}
                          active={yAxisType === value}
                        >
                          <Group wrap="nowrap">
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.75rem",
                                width: "100%",
                              }}
                            >
                              <span style={{ width: "0.75rem" }}>{icon}</span>
                              <Text size="sm">{label}</Text>
                            </div>
                          </Group>
                        </Combobox.Option>
                      );
                    }
                  )}
                </Combobox.Options>
              </Combobox.Dropdown>
            </Combobox>
          }
        >
          {t("chart_options_menu.y_axis_type.label")}
        </Menu.Item>
        <Menu.Item
          component="div"
          rightSection={
            <SegmentedControl
              size="xs"
              data={[
                {
                  label: t("chart_options_menu.x_axis_type.distance"),
                  value: "distance",
                },
                {
                  label: t("chart_options_menu.x_axis_type.time"),
                  value: "time",
                },
              ]}
              onChange={(value) => {
                if (!disabledXAxisToggle) setXAxisType(value);
              }}
              value={xAxisType ?? undefined}
              disabled={disabledXAxisToggle}
            />
          }
        >
          {t("chart_options_menu.x_axis_type.label")}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};
